import React, { useEffect, useState } from 'react';
import { FolderIcon, PlusIcon, EditIcon, TrashIcon, CheckIcon, XIcon, SearchIcon } from 'lucide-react';
import { getAllCategories, createCategory, updateCategory, deleteCategory, Category } from '../../services/categories';
const CategoryManager: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // For create/edit form
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCategory, setCurrentCategory] = useState<Partial<Category>>({
    name: '',
    description: ''
  });
  // For delete confirmation
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  useEffect(() => {
    fetchCategories();
  }, []);
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const fetchedCategories = await getAllCategories();
      setCategories(fetchedCategories);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setCurrentCategory(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentCategory.name?.trim()) {
      setError('Category name is required');
      return;
    }
    try {
      if (isEditing && currentCategory.id) {
        // Update existing category
        await updateCategory(currentCategory.id, currentCategory);
        setSuccessMessage('Category updated successfully!');
      } else {
        // Create new category
        await createCategory(currentCategory);
        setSuccessMessage('Category created successfully!');
      }
      // Reset form and fetch updated categories
      resetForm();
      fetchCategories();
      // Clear success message after delay
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Error saving category:', err);
      setError('Failed to save category. Please try again.');
    }
  };
  const handleEdit = (category: Category) => {
    setCurrentCategory(category);
    setIsEditing(true);
    setIsFormOpen(true);
  };
  const handleDelete = async (id: string) => {
    try {
      await deleteCategory(id);
      setCategories(categories.filter(cat => cat.id !== id));
      setConfirmDelete(null);
      setSuccessMessage('Category deleted successfully!');
      // Clear success message after delay
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Error deleting category:', err);
      setError('Failed to delete category. Please try again.');
    }
  };
  const resetForm = () => {
    setCurrentCategory({
      name: '',
      description: ''
    });
    setIsEditing(false);
    setIsFormOpen(false);
    setError(null);
  };
  // Filter categories by search term
  const filteredCategories = categories.filter(category => category.name.toLowerCase().includes(searchTerm.toLowerCase()) || category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()));
  return <div data-id="element-289">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6" data-id="element-290">
        <h1 className="text-2xl font-bold text-navy mb-4 md:mb-0 font-montserrat" data-id="element-291">
          Category Management
        </h1>
        <button onClick={() => setIsFormOpen(true)} className="btn-primary inline-flex items-center" disabled={isFormOpen} data-id="element-292">
          <PlusIcon size={18} className="mr-2" data-id="element-293" />
          Add New Category
        </button>
      </div>
      {error && <div className="bg-red-50 text-red-700 p-4 rounded-md mb-6" data-id="element-294">
          {error}
        </div>}
      {successMessage && <div className="bg-green-50 text-green-700 p-4 rounded-md mb-6 flex items-center" data-id="element-295">
          <CheckIcon size={18} className="mr-2" data-id="element-296" />
          {successMessage}
        </div>}
      {/* Create/Edit Form */}
      {isFormOpen && <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-297">
          <h2 className="text-xl font-bold text-navy mb-4 font-montserrat" data-id="element-298">
            {isEditing ? 'Edit Category' : 'Create New Category'}
          </h2>
          <form onSubmit={handleSubmit} data-id="element-299">
            <div className="mb-4" data-id="element-300">
              <label htmlFor="name" className="block mb-2 font-medium" data-id="element-301">
                Category Name *
              </label>
              <input type="text" id="name" name="name" value={currentCategory.name} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" placeholder="Enter category name" required data-id="element-302" />
            </div>
            <div className="mb-4" data-id="element-303">
              <label htmlFor="description" className="block mb-2 font-medium" data-id="element-304">
                Description
              </label>
              <textarea id="description" name="description" value={currentCategory.description || ''} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" placeholder="Enter category description" rows={3} data-id="element-305" />
            </div>
            <div className="flex justify-end space-x-3" data-id="element-306">
              <button type="button" onClick={resetForm} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-307">
                Cancel
              </button>
              <button type="submit" className="px-4 py-2 bg-orange text-white rounded-md hover:bg-orange-600 flex items-center" data-id="element-308">
                <CheckIcon size={18} className="mr-2" data-id="element-309" />
                {isEditing ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>}
      {/* Categories List */}
      <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-310">
        <div className="flex justify-between items-center mb-6" data-id="element-311">
          <h2 className="text-xl font-bold text-navy font-montserrat flex items-center" data-id="element-312">
            <FolderIcon size={20} className="mr-2" data-id="element-313" />
            Categories
          </h2>
          <div className="relative w-full md:w-64" data-id="element-314">
            <input type="text" placeholder="Search categories..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-315" />
            <SearchIcon size={18} className="absolute left-3 top-2.5 text-gray-400" data-id="element-316" />
          </div>
        </div>
        {loading ? <div className="flex justify-center py-12" data-id="element-317">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-navy" data-id="element-318"></div>
          </div> : filteredCategories.length === 0 ? <div className="text-center py-8" data-id="element-319">
            <FolderIcon size={36} className="mx-auto text-gray-400 mb-2" data-id="element-320" />
            <p className="text-gray-500" data-id="element-321">
              {searchTerm ? 'No categories match your search.' : 'No categories found.'}
            </p>
            {!searchTerm && <button onClick={() => setIsFormOpen(true)} className="mt-4 px-4 py-2 bg-navy text-white rounded-md hover:bg-navy-800 inline-flex items-center" data-id="element-322">
                <PlusIcon size={18} className="mr-2" data-id="element-323" />
                Create First Category
              </button>}
          </div> : <div className="overflow-x-auto" data-id="element-324">
            <table className="w-full" data-id="element-325">
              <thead className="bg-gray-50" data-id="element-326">
                <tr data-id="element-327">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-328">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-329">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-330">
                    Slug
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-331">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-332">
                {filteredCategories.map(category => <tr key={category.id} className="hover:bg-gray-50" data-id="element-333">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-334">
                      <div className="text-sm font-medium text-navy" data-id="element-335">
                        {category.name}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-336">
                      <div className="text-sm text-gray-500 truncate max-w-xs" data-id="element-337">
                        {category.description || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-338">
                      <div className="text-sm text-gray-500" data-id="element-339">
                        {category.slug}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-340">
                      <div className="flex justify-end space-x-2" data-id="element-341">
                        <button onClick={() => handleEdit(category)} className="text-blue-600 hover:text-blue-900" title="Edit" data-id="element-342">
                          <EditIcon size={18} data-id="element-343" />
                        </button>
                        <button onClick={() => setConfirmDelete(category.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-344">
                          <TrashIcon size={18} data-id="element-345" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>
      {/* Delete Confirmation Modal */}
      {confirmDelete && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-346">
          <div className="bg-white rounded-lg p-6 max-w-md w-full" data-id="element-347">
            <h3 className="text-xl font-bold text-navy mb-4" data-id="element-348">Confirm Delete</h3>
            <p className="mb-6" data-id="element-349">
              Are you sure you want to delete this category? This action cannot
              be undone.
            </p>
            <div className="flex justify-end space-x-3" data-id="element-350">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-351">
                Cancel
              </button>
              <button onClick={() => confirmDelete && handleDelete(confirmDelete)} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-id="element-352">
                Delete
              </button>
            </div>
          </div>
        </div>}
    </div>;
};
export default CategoryManager;